import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { mediaProxyUrl } from 'js/src/libs/media-proxy';
import styles from 'css/src/components/agent/BrandKitResults.module.scss';

import React from 'react';
import { z } from 'zod';

import type { BrandKitData } from '../../registries/BrandKitRegistry';
import { BrandKitRegistry } from '../../registries/BrandKitRegistry';
import type {
    CustomRendererComponentProps,
    EnhancedToolResult,
    ImageContent,
    TextContent,
    ToolExecutionContext,
} from '../../types';
import { CustomRendererMode } from '../../types';
import { Tool } from '../Tool';

import {
    convertImageToDataUrl,
    deduplicateColors,
    downscaleDataUrl,
    getTopLogoCandidates,
} from './brandKitHeuristics';

export const extractBrandSchema = z.object({
    url: z.string().describe('The website URL to extract brand elements from.'),
});
export type ExtractBrandArgs = z.infer<typeof extractBrandSchema>;

const WORKER_URL = 'https://extract-brand-kit.lumen5.com/';

function buildTextRepresentation(data: BrandKitData, extractionId: string): string {
    const summary: Record<string, unknown> = {
        extractionId,
        url: data.url,
        meta: data.meta,
        fonts: {
            heading: data.fonts.heading,
            body: data.fonts.body,
            loadedFamilies: data.fonts.loadedFamilies,
        },
        colors: {
            fromScreenshot: {
                brand: data.colors.fromScreenshot.brand.map((c) => ({
                    hex: c.hex,
                    population: c.population,
                })),
                neutral: data.colors.fromScreenshot.neutral.map((c) => ({
                    hex: c.hex,
                    population: c.population,
                })),
            },
            fromUI: data.colors.fromUI,
            fromCSS: data.colors.fromCSS,
        },
        logos: data.logos.map((logo, index) => ({
            index,
            source: logo.source,
            alt: logo.alt,
            width: logo.width,
            height: logo.height,
        })),
    };

    return JSON.stringify(summary, null, 2);
}

export class ExtractBrandTool extends Tool<typeof extractBrandSchema> {
    protected override readonly schema = extractBrandSchema;
    protected override readonly toolName = TOOL_NAMES.extractRawBrandData;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Fetch RAW brand data (colors, fonts, logos, screenshot) from a website URL. Returns unprocessed extraction results that you MUST then analyze and curate into a final brand kit. You can call this tool multiple times in parallel for different URLs.';

    public override canRunAsync = true;
    public override customRendererMode = CustomRendererMode.INLINE_HIDDEN;
    public override customRenderer = ExtractBrandResultRenderer;

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const args = JSON.parse(toolCallArgs) as { url?: string };
                if (args.url) {
                    const parsed = new URL(args.url);
                    const host = parsed.hostname.replace(/^www\./, '');
                    const path = parsed.pathname === '/' ? '' : parsed.pathname;
                    return `Scanning ${host}${path}`;
                }
            } catch {
                // fall through to default
            }
        }
        return 'Scanning website';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(
        args: ExtractBrandArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { url } = args;

        try {
            const response = await fetch(`${WORKER_URL}?url=${encodeURIComponent(url)}`);

            if (!response.ok) {
                const errorBody = await response.text();
                return {
                    result: `Error extracting brand kit: HTTP ${response.status} - ${errorBody}`,
                    isSuccess: false,
                };
            }

            const data: BrandKitData | { error: string } = await response.json();

            if ('error' in data) {
                return {
                    result: `Error extracting brand kit: ${data.error}`,
                    isSuccess: false,
                };
            }

            const registry = BrandKitRegistry.getInstance();
            const extractionId = registry.addExtraction(data);

            const textResult = buildTextRepresentation(data, extractionId);

            // Downscale screenshot to reduce payload size for the LLM context
            const compressedScreenshot = await downscaleDataUrl(data.screenshot.dataUrl, 720);

            // Build multipart tool result: structured JSON + full page screenshot + logo images
            const resultContent: (TextContent | ImageContent)[] = [
                { type: 'text', text: textResult },
                { type: 'text', text: '[Full page screenshot]:' },
                { type: 'image_url', image_url: { url: compressedScreenshot } },
            ];

            const topLogos = getTopLogoCandidates(data.logos, 3);
            for (const logo of topLogos) {
                const logoIndex = data.logos.indexOf(logo);
                // Always rasterize + downscale to 256px. Native png/jpeg logos must be shrunk too,
                // not just the svg/ico ones that need conversion — otherwise a full-resolution
                // og-image fallback rides through at full size and bloats the tool result.
                const imageUrl = await convertImageToDataUrl(mediaProxyUrl(logo.src), 256);
                if (!imageUrl) continue;
                const logoLabel = logo.alt
                    ? `[Logo candidate #${logoIndex} — source: ${logo.source}, alt: "${logo.alt}"]:`
                    : `[Logo candidate #${logoIndex} — source: ${logo.source}]:`;
                resultContent.push(
                    { type: 'text', text: logoLabel },
                    { type: 'image_url', image_url: { url: imageUrl } },
                );
            }

            return {
                result: textResult,
                resultContent,
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return {
                result: `Error extracting brand kit: ${errorMessage}`,
                isSuccess: false,
            };
        }
    }
}

const ExtractBrandResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }

    let parsed: { extractionId?: string } | null = null;
    try {
        parsed = JSON.parse(result);
    } catch {
        return <div>{result}</div>;
    }

    const extractionId = parsed?.extractionId;
    if (!extractionId) {
        return <div>{result}</div>;
    }

    const registry = BrandKitRegistry.getInstance();
    const data = registry.getExtraction(extractionId);
    if (!data) {
        return <div>Brand kit data not found.</div>;
    }

    // Deduplicate and filter colors for preview display
    const allColors = [
        ...data.colors.fromUI.map((c) => c.hex),
        ...data.colors.fromScreenshot.brand.map((c) => c.hex),
    ];
    const uniqueColors = deduplicateColors([...new Set(allColors)]);

    return (
        <div>
            <div className="mb-l5-8">
                <img
                    src={data.screenshot.dataUrl}
                    alt={`Screenshot of ${data.url}`}
                    className={styles['screenshot-preview']}
                />
            </div>

            {data.logos.length > 0 && (
                <div className="mb-l5-8">
                    <div className="fw-bold small mb-l5-4">Logos</div>
                    <div className={styles['logo-row']}>
                        {data.logos.slice(0, 6).map((logo, index) => (
                            <img
                                key={index}
                                src={mediaProxyUrl(logo.src)}
                                alt={logo.alt || `Logo ${index}`}
                                className={styles['logo-thumbnail']}
                            />
                        ))}
                    </div>
                </div>
            )}

            {uniqueColors.length > 0 && (
                <div className="mb-l5-8">
                    <div className="fw-bold small mb-l5-4">Colors</div>
                    <div className={styles['color-swatches']}>
                        {uniqueColors.slice(0, 12).map((hex, index) => (
                            <div
                                key={index}
                                className={styles['color-swatch']}
                                style={{ backgroundColor: hex }}
                                title={hex}
                            />
                        ))}
                    </div>
                </div>
            )}

            <div>
                <div className="fw-bold small mb-l5-4">Fonts</div>
                <div className={styles['font-label']}>
                    {data.fonts.heading && <div>Heading: {data.fonts.heading}</div>}
                    {data.fonts.body && <div>Body: {data.fonts.body}</div>}
                    {!data.fonts.heading && !data.fonts.body && <div>No fonts detected</div>}
                </div>
            </div>
        </div>
    );
};
